import React from 'react';
import { withRouter } from 'react-router';
import constants from '../../common/constants';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import BrowserHistory from '../utils/BrowserHistory';
import ContentViewer from './ContentViewer';
import DraftViewerRole from '../utils/DraftViewerRole';
import { Container, Header, Icon, Box } from '@amzn/awsui-components-core';
import {
  ReviewRequestStatus,
  UserRoles,
  WorkflowEventTypes,
} from '@amzn/awscat-pattern-library-service-model';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import { createWorkflowEventWithStatePolling } from '../../api/CreateWorkflowEventWithStatePolling';
import { ShowAllCommentsButton } from '../inline-comment';

class ReviewerContentViewer extends ContentViewer {
  protected showSectionalComments = true;
  protected draftViewerRole = DraftViewerRole.Reviewer;
  protected showLastBarRaiserEvaluation: boolean = true;

  protected canDisplayView(): boolean {
    const { authnInfo } = this.props.appContext;
    const roles = authnInfo?.userProfile.roles;
    return this.state.isPeerReviewer || (!!roles && roles.has(UserRoles.Admin));
  }

  protected showReviewFeedback = () => {
    return true;
  };

  // @ts-expect-error TS2416 - strict migration
  protected renderActions = () => {
    const { reviewRequestStatus, isPeerReviewer } = this.state;
    if (isPeerReviewer) {
      switch (reviewRequestStatus) {
        case ReviewRequestStatus.PENDING_ACCEPTANCE:
          return this.renderAcceptAndDeclineControls();
        case ReviewRequestStatus.ACCEPTED:
          return this.renderReviewDecisionInfo(constants.ALREADY_ACCEPTED_REVIEW_TEXT);
        case ReviewRequestStatus.APPROVED:
          return this.renderReviewDecisionInfo(constants.ALREADY_APPROVED_CONTENT_TEXT);
        case ReviewRequestStatus.DISAPPROVED:
          return this.renderReviewDecisionInfo(constants.ALREADY_DISAPPROVED_CONTENT_TEXT);
        default:
          // ASSIGNED: do nothing because content is in draft/unmodified state
          return null;
      }
    } else {
      return null;
    }
  };

  // overrides method in WorkflowView
  protected getBreadcrumbItems(): Array<{ text: string; href: string }> {
    return [
      {
        href: `${RouterPaths.CONTENT_TO_REVIEW}`,
        text: constants.REVIEWER_CONTENTS_HEADER,
      },
    ];
  }

  protected navigateBack = async () => {
    this.navigateToMyPendingReviews();
  };

  private renderAcceptAndDeclineControls = () => {
    return (
      <Container header={<Header variant="h3">Actions</Header>}>
        <ModalDialogButtonV3
          fullwidth
          variant="primary"
          header={'Accept review request'}
          text={constants.REVIEW_REQUEST_ACCEPT_MODAL_TEXT}
          onSubmit={this.acceptReview}
          submitButtonText={'Accept review'}
        />
        <Box margin={{ vertical: 's' }}>
          <ModalDialogButtonV3
            fullwidth
            variant="normal"
            header={'Decline review request'}
            text={constants.REVIEW_REQUEST_DECLINE_MODAL_TEXT}
            onSubmit={this.declineReview}
            submitButtonText={'Decline'}
          />
        </Box>
      </Container>
    );
  };

  private handleShowAllComments = () => {
    this.setShowInlineCommentsInReviewerPage(true);
  };

  private renderInlineComments = () => {
    return (
      <>
        <Box padding={{ top: 's' }}>
          <ShowAllCommentsButton handleShowAllComments={this.handleShowAllComments} />
        </Box>
      </>
    );
  };

  private renderReviewDecisionInfo = (infoText: string) => {
    return (
      <Container header={<Header variant="h3">Actions</Header>}>
        <Icon name="status-positive" size="normal" variant="success" />
        <Box display="inline" variant="p">
          {' '}
          {infoText}{' '}
        </Box>
        {this.renderInlineComments()}
      </Container>
    );
  };

  private navigateToMyPendingReviews = async () => {
    BrowserHistory.push(`${RouterPaths.CONTENT_TO_REVIEW}`, { keepFlashbarMessages: true });
  };

  private acceptReview = async () => {
    const { workflow } = this.state;
    try {
      await createWorkflowEventWithStatePolling(
        // @ts-expect-error TS18048 - strict migration
        workflow.contentId,
        // @ts-expect-error TS18048 - strict migration
        workflow.draftWorkflowId,
        WorkflowEventTypes.ACCEPT_REVIEW,
        '',
      );
      this.flashbarMessageBroker.pushSuccessMessage(constants.REVIEW_REQUEST_ACCEPTED_TEXT);
      window.location.reload();
    } catch (error) {
      this.handleError(error);
    }
  };

  private declineReview = async () => {
    const { workflow } = this.state;
    try {
      await createWorkflowEventWithStatePolling(
        // @ts-expect-error TS18048 - strict migration
        workflow.contentId,
        // @ts-expect-error TS18048 - strict migration
        workflow.draftWorkflowId,
        WorkflowEventTypes.DECLINE_REVIEW,
        '',
      );
      this.flashbarMessageBroker.pushSuccessMessage(constants.REVIEW_REQUEST_DECLIEND_TEXT);
      this.navigateToMyPendingReviews();
    } catch (error) {
      this.handleError(error);
    }
  };

  private handleError = (error: any) => {
    if (error?.response?.status === 400 || error?.response?.status === 403) {
      this.flashbarMessageBroker.pushWarnMessage(constants.REVIEW_REQUEST_ALREADY_DECLINED_TEXT);
    } else {
      throw error;
    }
  };
}

export default withRouter(withAppContext(ReviewerContentViewer));
